import styled from '@emotion/styled'
import Artplayer from '@msb-next/artplayer'
import { motion } from 'framer-motion'
import Hls from 'hls.js'
import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useRecoilValue } from 'recoil'
import { throttle } from 'throttle-debounce'

import useGetRecoilState from '@/hooks/useGetRecoilState'
import { rtmClient } from '@/pages/room'
import { CoursewareList, VideoCoursewareList } from '@/store/courseware'
import { roomReadyState, syncStateState } from '@/store/room'

Artplayer.DBCLICK_FULLSCREEN = false

interface IProps {
  course: CoursewareList | VideoCoursewareList
  url: string
  videoType: 1 | 2 // 1课件视频 2老视频
  poster?: string
  autoplay: boolean
}

interface HlsArtplayer extends Artplayer {
  hls?: Hls
}

export default function Index({ course, videoType, url, poster, autoplay }: IProps) {
  const videoRef = useRef(null) // 播放器DOM
  const artRef = useRef<HlsArtplayer | null>(null)
  const [ready, setReady] = useState(false)
  const roomReady = useRecoilValue(roomReadyState)
  const [, , getSyncState] = useGetRecoilState(syncStateState)

  useEffect(() => {
    rtmClient.on('ArtSyncAllstate', ArtSyncAllstate)
    rtmClient.on('ArtSyncVideo', ArtSyncVideo)
    rtmClient.on('ArtConVideo', ArtConVideo)
    rtmClient.on('ArtSayHello', ArtSayHello)
    rtmClient.on('ArtSayBye', ArtSayHello) // 和ArtSayHello同逻辑
    rtmClient.on('ArtNotifyMessage', ArtNotifyMessage)
    return () => {
      rtmClient.removeListener('ArtSyncAllstate', ArtSyncAllstate)
      rtmClient.removeListener('ArtSyncVideo', ArtSyncVideo)
      rtmClient.removeListener('ArtConVideo', ArtConVideo)
      rtmClient.removeListener('ArtSayHello', ArtSayHello)
      rtmClient.removeListener('ArtSayBye', ArtSayHello)
      rtmClient.removeListener('ArtNotifyMessage', ArtNotifyMessage)
    }
  }, [])

  const ArtSyncAllstate = useCallback((memberId: string, payload: any) => {
    // rtmClient.sendMessage('ArtSyncVideo', {
    //   seekToTime: artRef.current?.currentTime,
    //   timeStamp: +new Date() / 1000,
    //   status: !artRef.current?.playing ? 2 : 1,
    //   videoType
    // })
  }, [])

  const ArtSyncVideo = useCallback((memberId: string, payload: any) => {
    const { seekToTime, status, isManual } = payload
    const art = artRef.current
    if (art) {
      art.currentTime = seekToTime || 0
      if (status !== 1) {
        art.pause()
      }
    }
  }, [])

  const ArtConVideo = useCallback((memberId: string, payload: any) => {
    const { videoType, type } = payload
    const art = artRef.current
    // 1 播放 2 暂停 3 关闭老视频窗口
    if (art) {
      if (type !== 1 && (videoType !== 1 || getSyncState() !== 'normal')) {
        art.pause()
      }
      if (type === 1 && (videoType !== 1 || getSyncState() !== 'normal')) {
        art.play()
      }
    }
  }, [])

  const ArtSayHello = useCallback((memberId: string, payload: any) => {
    const { userInfo } = payload
    const { userId, userType } = userInfo || {}
    if (userType && Number(userType) === 3) return
    const art = artRef.current
    // 对端进入暂停视频
    if (art) {
      art.pause()
    }
  }, [])

  const ArtNotifyMessage = useCallback((memberId: string, payload: any) => {
    const { notice } = payload
    const art = artRef.current
    // 对端进入暂停视频
    if (art?.playing) {
      toast(notice)
    }
  }, [])

  useEffect(() => {
    if (url && videoRef.current) {
      artRef.current = new Artplayer({
        container: videoRef.current as unknown as HTMLDivElement,
        url: url,
        poster: poster || '',
        type: 'm3u8',
        customType: {
          m3u8: playM3u8
        }
      })
      subscribeClientEvents(artRef.current)
    }
    return () => {
      setReady(false)
      artRef.current?.destroy(false)
    }
  }, [url])

  useEffect(() => {
    if (artRef.current && ready && roomReady) {
      if (autoplay) {
        if (!artRef.current.playing) {
          artRef.current.play()
        }
      } else {
        if (artRef.current.playing) {
          artRef.current.pause()
        }
      }
    }
  }, [ready, autoplay, roomReady])

  const playM3u8 = (video: HTMLVideoElement, url: string, art: HlsArtplayer) => {
    if (Hls.isSupported()) {
      if (art.hls) art.hls.destroy()
      const hls = new Hls()
      hls.loadSource(url)
      hls.attachMedia(video)
      art.hls = hls
      art.on('destroy', () => hls.destroy())
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = url
    } else {
      art.notice.show = 'Unsupported playback format: m3u8'
    }
  }

  const subscribeClientEvents = (art: HlsArtplayer) => {
    art.on('ready', () => {
      // console.info(art.hls)
      setReady(true)
    })
    art.on(
      'video:timeupdate',
      throttle(5000, () => {
        // rtmClient.sendMessage('ArtSyncVideo', {
        //   seekToTime: artRef.current?.currentTime,
        //   timeStamp: +new Date() / 1000,
        //   status: !artRef.current?.playing ? 2 : 1,
        //   videoType
        // })
      })
    )
    art.on('video:seeking', () => {
      // rtmClient.sendMessage('ArtSyncVideo', {
      //   isManual: true,
      //   seekToTime: artRef.current?.currentTime,
      //   timeStamp: +new Date() / 1000,
      //   status: !artRef.current?.playing ? 2 : 1,
      //   videoType
      // })
    })
    art.on('video:play', () => {
      // rtmClient.sendMessage('ArtConVideo', {
      //   type: 1,
      //   timeStamp: +new Date() / 1000,
      //   videoType
      // })
    })
    art.on('video:pause', () => {
      // rtmClient.sendMessage('ArtConVideo', {
      //   type: 2,
      //   timeStamp: +new Date() / 1000,
      //   videoType
      // })
    })
    art.on('video:ended', () => {
      art.currentTime = 0
      const posterDom = document.querySelector('.art-poster') as HTMLDivElement
      posterDom.style.display = 'initial'
    })
    art.on('video:error', (error) => {
      console.log('video:error', error)
    })
    art.on('error', (error) => {
      console.log('art error', error)
    })
  }

  return (
    <Container className="w-full h-full">
      <div ref={videoRef} className="w-full h-full"></div>
    </Container>
  )
}

const Container = styled(motion.div)``
